import { HotPlaylistsStateModel } from '@store/hot-playlists/hot-playlists.state.model';
import { Selector, State } from '@ngxs/store';
import { itemsPerPage } from '@shared/helpers';
import { Observable } from 'rxjs';
import { IPlaylistHot } from '@interfaces/playlist';
import { PlaylistsService } from '@services/playlists/playlists.service';
import { IResponse } from '@interfaces/response';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { DataActionWithoutSubscription } from '@shared/functions';
import {CookieService} from 'ngx-cookie';

@StateRepository()
@State<HotPlaylistsStateModel>({
    name: 'hotPlaylistPageState',
    defaults: {
        itemsPerPage: itemsPerPage[5],
        currentPage: 1,
        searchParam: '',
        totalPlaylists: 0,
        playlists: [],
        loader: false,
        order: '-created_at'
    }
})

@Injectable()
export class HotPlaylistsState extends NgxsDataRepository<HotPlaylistsStateModel> {
    constructor(
        private cookie: CookieService,
        private playlistsService: PlaylistsService
    ) {
        super();
    }

    @Selector([HotPlaylistsState])
    static hotPlaylists(state: HotPlaylistsStateModel): IPlaylistHot[] {
        return state.playlists ?? [];
    }

    @Selector([HotPlaylistsState])
    static hotPlaylistsTotal(state: HotPlaylistsStateModel): number {
        return state.totalPlaylists ?? 0;
    }

    @Selector([HotPlaylistsState])
    static hotPlaylistsCurrentPage(state: HotPlaylistsStateModel): number {
        return state.currentPage ?? 1;
    }

    @Selector([HotPlaylistsState])
    static hotPlaylistsItemsPerPage(state: HotPlaylistsStateModel): number {
        return state.itemsPerPage ?? itemsPerPage[5];
    }

    @Selector([HotPlaylistsState])
    static hotPlaylistsSearchParam(state: HotPlaylistsStateModel): string {
        return state.searchParam ?? '';
    }

    @Selector([HotPlaylistsState])
    static hotPlaylistsLoader(state: HotPlaylistsStateModel): boolean {
        return state.loader;
    }

    @DataActionWithoutSubscription({cancelUncompleted: true})
    fetchHotPlaylists(): Observable<IResponse<IPlaylistHot>> {
        const limit = this.ctx.getState().itemsPerPage;
        const skip = limit * (this.ctx.getState().currentPage - 1);
        const search = this.ctx.getState().searchParam;
        const order = this.ctx.getState().order;
        return this.playlistsService.fetchPlaylists({
            limit,
            skip,
            search,
            order,
            type: 'hot'
        }).pipe(
            tap((response) => {
                const state = this.ctx.getState();
                this.ctx.setState({...state, playlists: response.rows, totalPlaylists: response.total});
            })
        );
    }

    @DataActionWithoutSubscription()
    searchHotPlaylists(@Payload('[Hot playlists page] search hot playlists') searchString: string): Observable<any> {
        this.ctx.patchState({searchParam: searchString, currentPage: 1});
        return this.fetchHotPlaylists();
    }

    @DataActionWithoutSubscription()
    setHotPlaylistsPerPage(@Payload('[Hot playlists page] set per page in hot playlists') perPage: number): Observable<any> {
        this.cookie.put('playlistsPerPage', perPage.toString());

        this.ctx.patchState({itemsPerPage: perPage, currentPage: 1});
        return this.fetchHotPlaylists();
    }

    @DataActionWithoutSubscription()
    changePage(@Payload('[Hot playlists page] change page') page: number): Observable<any> {
        this.ctx.patchState({currentPage: page});
        return this.fetchHotPlaylists();
    }

    @DataActionWithoutSubscription()
    orderHotPlaylists(@Payload('[Hot playlists page] order playlists') order: string): Observable<any> {
        this.ctx.patchState({order});
        return this.fetchHotPlaylists();
    }
}
